// src/components/songEdit/modals/AddFileAfterSaveModal.tsx
import React, { useState, useRef } from 'react';
import apiClient from '../../../utils/apiClient';
import { toast } from 'react-toastify';

interface AddFileAfterSaveModalProps {
  isOpen: boolean;
  onClose: () => void;
  songId: number;
  songTitle: string;
  versionId: number;
}

const FILE_TYPES = [
  { value: 'bulletin', label: 'Bulletin (congregation)' },
  { value: 'choir', label: 'Choir' },
  { value: 'chart', label: 'Chart' },
  { value: 'instrumental', label: 'Instrumental' },
  { value: 'misc', label: 'Misc' }
];

export const AddFileAfterSaveModal: React.FC<AddFileAfterSaveModalProps> = ({
  isOpen,
  onClose,
  songId,
  songTitle,
  versionId
}) => {
  const [step, setStep] = useState<'prompt' | 'upload'>('prompt');
  const [selectedFileType, setSelectedFileType] = useState('bulletin');
  const [uploading, setUploading] = useState(false);
  const [uploadComplete, setUploadComplete] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleYes = () => {
    setStep('upload');
  };

  const handleNo = () => {
    onClose();
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file extension before uploading
    const validExtensions = ['pdf', 'png', 'jpg', 'jpeg', 'tif', 'tiff'];
    const ext = file.name.split('.').pop()?.toLowerCase() || '';
    if (!validExtensions.includes(ext)) {
      toast.error(`Invalid file type. Allowed: ${validExtensions.join(', ')}`);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setUploading(true);
    try {
      // Step 1: Upload file to temp location
      const formData = new FormData();
      formData.append('file', file);

      const uploadResponse = await apiClient.post('/file-management/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (!uploadResponse.data.success) {
        throw new Error(uploadResponse.data.error || 'Upload failed');
      }

      const tempPath = uploadResponse.data.tempPath;

      // Step 2: Rename and move file with canonical naming
      const renameResponse = await apiClient.post('/file-management/rename', {
        songId: songId,
        versionId: versionId,
        category: selectedFileType,
        originalFilePath: tempPath,
        performanceType: selectedFileType === 'bulletin' ? 'CONG' : selectedFileType.toUpperCase(),
        versionNumber: '1'
      });

      if (renameResponse.data.success) {
        toast.success(`File added: ${renameResponse.data.canonicalFilename}`);
        setUploadComplete(true);
      } else {
        throw new Error(renameResponse.data.error || 'Rename failed');
      }
    } catch (err: any) {
      console.error('[AddFileAfterSaveModal] Upload error:', err);
      toast.error(err.response?.data?.message || err.message || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleAddAnother = () => {
    setUploadComplete(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDone = () => {
    setStep('prompt');
    setUploadComplete(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              {step === 'prompt' ? 'Song Saved!' : 'Add File'}
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={handleDone}
              disabled={uploading}
            ></button>
          </div>

          <div className="modal-body">
            {step === 'prompt' && (
              <div className="text-center">
                <p className="mb-3">
                  <strong>"{songTitle}"</strong> has been saved successfully.
                </p>
                <p>Would you like to add a file to this song?</p>
              </div>
            )}

            {step === 'upload' && !uploadComplete && (
              <div>
                <p className="mb-3">
                  Select a file type and choose a file to upload:
                </p>

                <div className="mb-3">
                  <label className="form-label">File Type</label>
                  <select
                    value={selectedFileType}
                    onChange={(e) => setSelectedFileType(e.target.value)}
                    className="form-select"
                    disabled={uploading}
                  >
                    {FILE_TYPES.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label">Choose File</label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg,.tif,.tiff"
                    onChange={handleFileUpload}
                    disabled={uploading}
                    className="form-control"
                  />
                  <small className="text-muted">
                    Supported: PDF, PNG, JPG, TIFF
                  </small>
                </div>

                {uploading && (
                  <div className="text-center">
                    <div className="spinner-border spinner-border-sm me-2" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    Uploading...
                  </div>
                )}
              </div>
            )}

            {step === 'upload' && uploadComplete && (
              <div className="text-center">
                <div className="text-success mb-3" style={{ fontSize: '3rem' }}>
                  ✓
                </div>
                <p>File uploaded successfully!</p>
                <p className="text-muted">Would you like to add another file?</p>
              </div>
            )}
          </div>

          <div className="modal-footer">
            {step === 'prompt' && (
              <>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleNo}
                >
                  No, I'm Done
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleYes}
                >
                  Yes, Add File
                </button>
              </>
            )}

            {step === 'upload' && !uploadComplete && (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleDone}
                disabled={uploading}
              >
                Cancel
              </button>
            )}

            {step === 'upload' && uploadComplete && (
              <>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleDone}
                >
                  Done
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleAddAnother}
                >
                  Add Another File
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddFileAfterSaveModal;
